import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../api/client";

/* ================= Types ================= */

interface StartSessionResponse {
  status: string;
  session_id: string;
  visitor_id: string;
}

/* ================= Utils ================= */

const getOrCreateVisitorId = (): string => {
  let id = localStorage.getItem("visitor_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("visitor_id", id);
    localStorage.setItem("first_visit", new Date().toISOString());
  }
  return id;
};

const getSessionId = (): string | null =>
  localStorage.getItem("session_id");

/* ================= Hook ================= */

export const useAutoPageTracking = () => {
  const location = useLocation();

  const lastPathRef = useRef<string>("");
  const pageStartTimeRef = useRef<number>(Date.now());
  const sessionStartedRef = useRef<boolean>(false);

  /* ---------- Start Session ---------- */
  const startSession = async () => {
    if (sessionStartedRef.current) return;

    const visitorId = getOrCreateVisitorId();

    try {
      const res = await api.post<StartSessionResponse>(
        "/analytics/session/start/",
        {
          visitor_id: visitorId,
          entry_page: window.location.pathname,
          referrer: document.referrer || "",
        }
      );

      localStorage.setItem("session_id", res.data.session_id);
      localStorage.setItem("session_start", Date.now().toString());

      sessionStartedRef.current = true;
    } catch (err) {
      console.error("Analytics start_session failed", err);
    }
  };

  /* ---------- Track Page View ---------- */
  const trackPageView = async (path: string, timeOnPage: number) => {
    const visitorId = getOrCreateVisitorId();
    const sessionId = getSessionId();
    if (!sessionId) return;

    try {
      await api.post("/analytics/page-view/", {
        visitor_id: visitorId,
        session_id: sessionId,
        path,
        title: document.title || "Untitled",
        referrer: document.referrer || "",
        time_on_page: timeOnPage,
        scroll_depth: calculateScrollDepth(),
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      });
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Analytics page_view error", err);
      }
    }
  };

  /* ---------- End Session ---------- */
  const endSession = async () => {
    const sessionId = getSessionId();
    if (!sessionId) return;

    try {
      await api.post("/analytics/session/end/", {
        session_id: sessionId,
        exit_page: window.location.pathname,
      });
    } catch (err) {
      console.error("Analytics end_session error", err);
    } finally {
      localStorage.removeItem("session_id");
      localStorage.removeItem("session_start");
      sessionStartedRef.current = false;
    }
  };

  /* ---------- Route Change Tracking ---------- */
  useEffect(() => {
    startSession();

    const currentPath =
      location.pathname + location.search + location.hash;

    if (currentPath === lastPathRef.current) return;

    const timeOnPreviousPage = Math.floor(
      (Date.now() - pageStartTimeRef.current) / 1000
    );

    if (lastPathRef.current) {
      trackPageView(lastPathRef.current, timeOnPreviousPage);
    }

    lastPathRef.current = currentPath;
    pageStartTimeRef.current = Date.now();
  }, [location.pathname, location.search, location.hash]);

  /* ---------- Page Exit / Refresh ---------- */
  useEffect(() => {
    const handleUnload = () => {
      const timeOnPage = Math.floor(
        (Date.now() - pageStartTimeRef.current) / 1000
      );

      if (lastPathRef.current) {
        navigator.sendBeacon(
          "/api/analytics/page-view/",
          JSON.stringify({
            visitor_id: getOrCreateVisitorId(),
            session_id: getSessionId(),
            path: lastPathRef.current,
            title: document.title,
            time_on_page: timeOnPage,
            scroll_depth: calculateScrollDepth(),
            user_agent: navigator.userAgent,
          })
        );
      }

      navigator.sendBeacon(
        "/api/analytics/session/end/",
        JSON.stringify({
          session_id: getSessionId(),
          exit_page: window.location.pathname,
        })
      );
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("pagehide", handleUnload);
    };
  }, []);
};

/* ================= Helpers ================= */

const calculateScrollDepth = (): number => {
  const scrollTop =
    window.pageYOffset || document.documentElement.scrollTop;
  const docHeight =
    document.documentElement.scrollHeight -
    document.documentElement.clientHeight;

  return docHeight > 0
    ? Math.round((scrollTop / docHeight) * 100)
    : 0;
};
